<template>
  <div
    id="app"
    :style="{
      background: variables[`${style}-container-background`],
      '--background': variables[`${style}-top-bar-background`],
      '--menuColor': variables[`${style}-menu-color`],
      '--topColor': variables[`${style}-tab-color`],
    }"
  >
    <keep-alive
      v-if="isEnvironmentValid"
      :include="['MyTask', 'TemplatePreview', 'homePage']"
    >
      <router-view />
    </keep-alive>
    <HtGuide v-else />
  </div>
</template>

<script>
  import variables from '@/styles/variables.scss'
  import { mapGetters } from 'vuex'
  import debounce from 'lodash/debounce'
  import { registerWatermark } from '@transsion/watermark'
  import { isFeishu } from '@/utils'
  import { checkTranslate } from '@/utils/autoTranslate'
  import { ZH_CODE } from '@transsion/hotent-ui/packages/Language'
  import req from '@/utils/request'

  export default {
    name: 'App',
    data() {
      return {
        variables,
        theme: {
          name: 'default',
          style: 'light',
          layout: 'vertical',
          themeColor: '#2F54EB',
        },
        unRegisterWatermarkFn: null,
        // 安全检测
        isEnvironmentValid: false,
      }
    },
    computed: {
      ...mapGetters({
        layout: 'settings/layout',
        style: 'settings/style',
        themeColor: 'settings/themeColor',
        username: 'user/username',
        account: 'user/account',
      }),
    },
    async created() {
      // 因为resize事件在整个项目中只能监听一次，所以这里通过全局广播发送这个事件
      window.onresize = debounce(() => {
        this.$root.$emit('resize')
      }, 300)

      // 弥补全屏显示时，样式未生效
      const theme = localStorage.getItem('eip-front-theme')
      if (theme !== null) {
        this.theme = JSON.parse(theme)
      } else {
        this.theme = {
          layout: this.layout,
          style: this.style,
          themeColor: this.themeColor,
        }
      }
      this.setDefaultTheme()

      // 首先检查sessionStorage中是否已有校验通过的状态
      const cachedValidation = sessionStorage.getItem('environmentValid')
      if (cachedValidation === 'true' || isFeishu) {
        this.isEnvironmentValid = true
        // 保存校验状态到sessionStorage
        sessionStorage.setItem('environmentValid', 'true')
      } else {
        // 验证环境
        await this.validateEnvironment()
      }
    },
    mounted() {
      window.addEventListener('message', ({ data }) => {
        if (data.type === 'router') {
          this.$router.push(data.url)
        }
      })

      // 注册水印
      this.initWaterMarker()

      // 检测是否包含中文
      this.isEnvironmentValid &&
        this.$nextTick(() => {
          const lang = localStorage.getItem('lang')

          if (lang !== ZH_CODE) {
            checkTranslate(lang.slice(0, 2))
          }
        })

      this.checkAccount()
    },
    beforeDestroy() {
      // 卸载水印
      this.unRegisterWatermarkFn && this.unRegisterWatermarkFn()
    },
    methods: {
      // 验证环境
      async validateEnvironment() {
        // 先查看URL中有没有拼接RegCID
        const urlParams = new URLSearchParams(window.location.search)
        const regCID = urlParams.get('RegCID')

        // 构建请求配置
        const requestConfig = {}
        if (regCID) {
          // 如果存在RegCID参数，则将其添加到请求头中
          requestConfig.headers = {
            other_allow_scene: regCID,
          }
        }

        try {
          const res = await req.post(
            window.context.uc + '/api/extControl/extAllowCheck',
            {},
            requestConfig
          )

          // 根据接口返回的code状态判断
          if (res.code === 200 && res.state === true) {
            this.isEnvironmentValid = true
            // 保存校验状态到sessionStorage
            sessionStorage.setItem('environmentValid', 'true')

            window.location.reload()
          } else {
            this.isEnvironmentValid = false
            // 清除可能存在的校验状态
            sessionStorage.removeItem('environmentValid')
          }
        } catch (e) {
          console.log(`根据是否有RegCID决定是否传递请求头: ${e}`)

          this.isEnvironmentValid = false
          // 清除可能存在的校验状态
          sessionStorage.removeItem('environmentValid')
        }
      },

      setDefaultTheme() {
        let { name, layout, style, themeColor } = this.theme
        localStorage.setItem(
          'eip-front-theme',
          JSON.stringify({
            name,
            layout,
            style,
            themeColor,
          })
        )
        document.getElementsByTagName(
          'body'
        )[0].className = `eip-front-theme-default`
        document.body.style = `--themeColor:${themeColor}; --themeColorRgb:${this.hexToRgba(
          themeColor
        )}`
      },
      hexToRgba(hex) {
        const red = parseInt(`0x${hex.slice(1, 3)}`)
        const green = parseInt(`0x${hex.slice(3, 5)}`)
        const blue = parseInt(`0x${hex.slice(5, 7)}`)
        return `${red},${green},${blue}`
      },
      initWaterMarker() {
        if (isFeishu) return

        const content = `${this.username} ${this.account}`
        this.unRegisterWatermarkFn = registerWatermark({
          content,
          excludeIframe: true,
        })
      },
      checkAccount() {
        const urlParams = new URLSearchParams(window.location.search)
        const accountParam = urlParams.get('account')
        if (accountParam && accountParam !== this.account) {
          this.$store.dispatch('user/toLoginUrl')
        }
      },
    },
  }
</script>
<style lang="scss" scoped>
  #app {
    min-height: 100%;
  }
  ::v-deep .form-table_box {
    .el-tooltip {
      display: inline-flex !important;
    }
  }
</style>
<style lang="scss">
  .online-form {
    .el-form:not(.el-form--label-top) {
      .ht-form-item {
        display: flex;
        flex-wrap: wrap;
        .el-form-item__label {
          width: auto;
          min-width: 120px;
          // word-break: break-all;
        }
        .el-form-item__content {
          flex: 1;
          min-width: 180px;
          margin-left: 0px !important;
        }
      }
    }
  }

  .ht-form-inputs__disabled {
    box-sizing: border-box;
    height: 32px;
    padding: 0 30px 0 15px;
    color: #000000 !important;
    cursor: not-allowed;
    background-color: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 2px;
    .el-input__inner {
      padding: 0;
      cursor: not-allowed;
      background-color: #f5f7fa;
      border: none;
    }
  }
  .el-input.is-disabled .el-input__inner {
    color: #000000 !important;
  }
</style>
